/**
 * Errors Navigation
 *
 * This file contains functions for navigating between errors in the UI.
 */

import { logError } from 'services/logging/logging';
import { waitForElement } from 'services/schemableFormNavigation/navigationHelpers';
import { GenericError } from 'support/helpers/errors/errors';

import { InvoiceDiscrepancy } from './types';

// Tables that use virtualization
const VIRTUALIZED_TABLES = ['line_items', 'matched_line_items', 'unmatched_line_items'];

/**
 * Scrolls to and focuses on the specified discrepancy element in the UI.
 */
export const scrollToDiscrepancy = async (
  error: InvoiceDiscrepancy,
  scrollToVirtualizedRow: (tableId: string, rowIndex: number) => void,
  containerRef?: React.RefObject<HTMLElement | null> | null,
): Promise<void> => {
  const root = getScopeRoot(containerRef);
  const selector = `[data-field="${error.id}"]`;
  let element = root.querySelector(selector);

  if (!element) {
    const table = VIRTUALIZED_TABLES.find((table) => error.id.startsWith(table));
    if (table) {
      element = await getVirtualizedItem(error.id, table, scrollToVirtualizedRow, containerRef);
    } else {
      logError(new GenericError('Discrepancies: Failed to find element with error: ', { error, selector }));
    }
  }

  if (element && 'focus' in element) {
    (element as HTMLInputElement).focus();
    element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
  }
};

const getScopeRoot = (containerRef?: React.RefObject<HTMLElement | null> | null): ParentNode => {
  return containerRef?.current ?? document;
};

/**
 * Gets a virtualized item from a container by its error ID.
 */
const getVirtualizedItem = async (
  errorId: string,
  domContainerId: string,
  scrollToVirtualizedRow: (tableId: string, rowIndex: number) => void,
  containerRef?: React.RefObject<HTMLElement | null> | null,
): Promise<Element | null> => {
  const root = getScopeRoot(containerRef);
  const virtualizedContainer = root.querySelector<HTMLElement>(`#${domContainerId}`);
  if (virtualizedContainer) {
    const index = getItemIndexSection(errorId);
    if (index !== -1) {
      scrollToVirtualizedRow(domContainerId, index);
    }
  }

  // Shared with WebEDI navigation: the target may not be in the DOM yet, because the virtualizer
  // has to scroll its row in and a collapsed embedded table has to expand, and both commit after
  // this call. Falls back to the container when the field never appears.
  return (await waitForElement(root, `[data-field="${errorId}"]`)) ?? virtualizedContainer;
};

/**
 * Gets the item index from an error ID (e.g., "line_items.0.quantity" -> 0).
 */
const getItemIndexSection = (errorId: string): number => {
  // error.id is in the format of line_items.0.quantity
  return parseInt(errorId.split('.')[1]);
};
